import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import gql from 'graphql-tag'

import { FormRow, FormCol } from 'style/styled'
import { minMaxVal } from 'helpers/validators'
import { Form, Checkbox, Number as NumberInput, Select, DateTimePicker } from 'components/forms'
import Button from 'components/Button'
import Divider from 'components/Divider'

const updateCashRegisterCurrencyRateMutation = gql`
mutation updateCashRegisterCurrencyRate($cashRegisterId: ID!, $currency: CurrencyTypeEnum!) {
  cashAssist {
		updateCashRegisterCurrencyRate(cashRegisterId: $cashRegisterId, currency: $currency) {
      id
      parameters {
        foreignCurrency {
          euro {
            exchangeRate
            exchangeRateDate
						exchangeRateIsUsed
          }
          w1 {
            exchangeRate
            exchangeRateDate
						exchangeRateIsUsed
          }
					w2 {
            exchangeRate
            exchangeRateDate
						exchangeRateIsUsed
          }
					w3 {
            exchangeRate
            exchangeRateDate
						exchangeRateIsUsed
          }
        }
      }
  	}
	}
}
`

const ForeignCurrency = ({ name, data, currencyField, itemId, apiRef }) => {
	const [t] = useTranslation()
	const [errorState, setErrorState] = useState(false)
	const [updateRate, { loading: loadingRate }] = useMutation(updateCashRegisterCurrencyRateMutation, {
		update: (cache, { data: mutateData }) => {
			const newExchangeRateIsUsed = mutateData?.cashAssist?.updateCashRegisterCurrencyRate.parameters.foreignCurrency[currencyField].exchangeRateIsUsed
			apiRef.current.setValue(`parameters.foreignCurrency.${currencyField}.exchangeRateIsUsed`, newExchangeRateIsUsed)
		}
	})
	return (
		<>
			<Divider/>
			<FormRow>
				<p style={{ margin: '0 0 7px 20px' }}>{name}
				</p>
			</FormRow>
			<FormRow>
				<FormCol>
					<DateTimePicker
						value={data?.parameters?.foreignCurrency?.[currencyField]?.exchangeRateDate}
						picker='date'
						noForm
						label={t('options_section.fields.currency_rate_date')}
						disabled
						shrink
						inline />
					<NumberInput
						value={data?.parameters?.foreignCurrency?.[currencyField]?.exchangeRate}
						label={t('options_section.fields.currency_rate')}
						noForm
						disabled
						shrink
						withControls={false}
						inline />
					<Button
						disabled={errorState}
						status={errorState ? 'inactive' : 'primary'}
						style={{ marginRight: '20px' }}
						onClick={() => updateRate({ variables: { cashRegisterId: itemId, currency: currencyField } })}
						loading={loadingRate}
						text={t('options_section.strings.currency_update_new_course')}
						inline />
					<Checkbox
						inline
						field={`parameters.foreignCurrency.${currencyField}.isDefinedMannualy`}
						label={`${t('options_section.fields.currency_defined_mannualy')}`} />
				</FormCol>
			</FormRow>
			<FormRow>
				<FormCol md>
					<NumberInput
						{...minMaxVal({ min: -100, max: 100, error: `${t('validators.value_between', { minVal: -100, maxVal: 100 })}`, setErrorState })}
						label={t('options_section.fields.currency_addition_percent')}
						field={`parameters.foreignCurrency.${currencyField}.additionPercent`}
						precision={2}
						withControls={false}
						shrink
						inline />
					{apiRef.current.getValue(`parameters.foreignCurrency.${currencyField}.isDefinedMannualy`) &&
						<NumberInput
							field={`parameters.foreignCurrency.${currencyField}.exchangeRateIsUsed`}
							label={t('options_section.fields.currency_exchange_rate_in_use')}
							precision={4}
							withControls={false}
							shrink
							inline />
					}
				</FormCol>
			</FormRow>
			{currencyField === 'euro' && <FormRow>
				<Divider/>
				<FormCol md>
					<Select
						field='parameters.foreignCurrency.euro.changeTo'
						label={t('options_section.fields.currency_payment_system')}
						options={[
							{ value: 'CHF', label: t('options_section.fields.currency_option_standard') },
							{ value: 'CHF_EUR', label: t('options_section.fields.currency_option_or_euro') },
							{ value: 'EUR', label: t('options_section.fields.currency_option_change_euro') }
						]}
					/>
					<Select
						field={`parameters.foreignCurrency.euro.roundTo`}
						label={t('options_section.fields.currency_round_to')}
						shrink
						options={[
							{ value: 'Cent1', label: 0.01 },
							{ value: 'Cent5', label: 0.05 },
							{ value: 'Cent10', label: 0.10 }
						]}
					/>
					<Checkbox
						field={`parameters.foreignCurrency.euro.isActive`}
						label={`${t('options_section.fields.currency_euro_active')}`} />
					<Checkbox
						field={`parameters.foreignCurrency.euro.isEuroCoinsNotShown`}
						label={`${t('options_section.fields.currency_coins_not_show')}`} />
					<Checkbox
						field={`parameters.foreignCurrency.euro.is200EuroNotShown`}
						label={`${t('options_section.fields.currency_two_hundred_not_show')}`} />
					<Checkbox field={`parameters.foreignCurrency.euro.isShowTotalEuroOnCustomerDisplay`}
						label={`${t('options_section.fields.currency_show_total_display')}`} />
				</FormCol>
			</FormRow>}
		</>
	)
}

const Currencies = ({ itemId, appData, data, formData, updateItem }) => {
	const apiRef = useRef(null)

	const submitForeignCurrency = vals => {
		const submitCurrencyValues = { ...vals }
		updateItem(submitCurrencyValues)
	}

	return (
		<Form
			apiRef={apiRef}
			initialValues={formData}
			onSubmit={submitForeignCurrency}
			smartSubmit
		>
			{appData.countryId === '1' && // show special eur currency only for Switzerland
				<ForeignCurrency name='EUR' data={data} itemId={itemId} currencyField='euro' apiRef={apiRef} />
			}
			{appData.innerNameCurrency1 &&
				<ForeignCurrency name={appData.innerNameCurrency1} data={data} itemId={itemId} currencyField='w1' apiRef={apiRef} />
			}
			{appData.innerNameCurrency2 &&
				<ForeignCurrency name={appData.innerNameCurrency2} data={data} itemId={itemId} currencyField='w2' apiRef={apiRef} />
			}
			{appData.innerNameCurrency3 &&
				<ForeignCurrency name={appData.innerNameCurrency3} data={data} itemId={itemId} currencyField='w3' />
			}
		</Form>
	)
}

export default Currencies
